import {
  RuntimeOperations,
  WidgetConfig,
  evaluateConditionGroup,
} from '@helo/cms'
import { ReactNode } from 'react'

import { ErrorBoundary } from '../../components/ErrorBoundary/ErrorBoundary'
import { WidgetError } from '../../components/Errors/WidgetError'
import { Role } from '../../config/roles'
import { useAppStore } from '../../store/appStore/appStore'
import { ColorMode } from '../../theme/ColorMode'
import { widgetRegistry } from '../widget-registry'
import { WidgetRegistryEntry } from '../widget-utils'
import {
  WidgetCustomPayloadContext,
  useWidgetCustomPayload,
} from './WidgetCustomPayloadContext'
import { WidgetProvider } from './WidgetProvider'
import {
  extractDependentQueryIds,
  getCanonicalWidgetProps,
  getStaticWidgetProps,
  resolveDependentQueryLoading,
} from './WidgetRenderer.parts'

interface InternalWidgetRendererProps {
  widgetConfig: WidgetConfig
  widgetDefinition: WidgetRegistryEntry
  children?: ReactNode
  customPayload?: Record<string, any>
}

const InternalWidgetRenderer = ({
  widgetConfig,
  widgetDefinition,
  children,
  customPayload,
}: InternalWidgetRendererProps) => {
  'use no memo'
  const Widget = widgetDefinition.widget
  const dependentQueryIds = extractDependentQueryIds(
    widgetConfig as any,
    widgetDefinition,
  )

  const {
    dependentQueries,
    errors,
    queriesPendingRegistration,
    someQueriesAreLoading,
  } = useAppStore(({ queryStore }) => {
    const queries = dependentQueryIds
      .map(queryId => queryStore.getQuery(queryId))
      .filter(queryNode => queryNode != null)

    return resolveDependentQueryLoading(dependentQueryIds, queries)
  })

  if (
    errors.length &&
    !widgetDefinition.manualQuery &&
    !widgetDefinition.internalLoading
  ) {
    console.error('[WidgetRenderer2] Dependent query errors', {
      widgetType: widgetConfig.type,
      errors,
    })
    const ErrorRenderer = widgetDefinition.errorRenderer || WidgetError
    return <ErrorRenderer message={errors.join('\n')} />
  }

  const slots =
    widgetConfig.slots && Object.keys(widgetConfig.slots).length > 0
      ? Object.fromEntries(
          Object.entries(widgetConfig.slots ?? {}).map(([key, value]) => {
            return [key, <WidgetRenderer2 widgetConfig={value} key={key} />]
          }),
        )
      : {}

  if (queriesPendingRegistration) {
    const pendingRegistrationProps = getStaticWidgetProps(
      getCanonicalWidgetProps(widgetConfig.state, widgetConfig.type),
    )

    return (
      <Widget
        isLoading={true}
        data-widget={widgetConfig.type}
        {...pendingRegistrationProps}
        {...slots}
      >
        {children}
      </Widget>
    )
  }

  const processedState = RuntimeOperations.processValue({
    context: customPayload,
    contentNode: null,
  } as any)(widgetConfig.state) as WidgetConfig['state']
  const processedProps = getCanonicalWidgetProps(
    processedState,
    widgetConfig.type,
  )
  const contentNode = processedProps?.contentNode
  const shouldShow = processedState?.showIf
    ? evaluateConditionGroup(processedState.showIf as any)
    : true

  if (!shouldShow) return null

  return (
    <Widget
      {...processedProps}
      isLoading={someQueriesAreLoading}
      data-widget={widgetConfig.type}
      {...(contentNode != null ? { contentNode } : {})}
      {...slots}
    >
      {children}
    </Widget>
  )
}

interface WidgetRenderer2Props {
  widgetConfig: WidgetConfig
  customPayload?: Record<string, any>
}

export const WidgetRenderer2 = ({
  widgetConfig,
  customPayload,
}: WidgetRenderer2Props) => {
  const hasPermissions = useAppStore(store =>
    store.auth.hasRole(widgetConfig.state?.roles as Role[] | undefined),
  )

  const widgetDefinition = widgetRegistry[widgetConfig.type]
  if (!widgetDefinition) {
    throw new Error(`Widget ${widgetConfig.type} not found`)
  }

  const currentCustomPayload = useWidgetCustomPayload()
  const mergedCustomPayload = { ...currentCustomPayload, ...customPayload }

  if (!hasPermissions) {
    return null
  }

  if (widgetConfig.state?.disabled) {
    return null
  }

  return (
    <ErrorBoundary
      renderError={error => {
        console.error('[WidgetRenderer2] Widget tree render error', {
          widgetType: widgetConfig.type,
          error,
        })
        return <WidgetError />
      }}
    >
      <ColorMode colorMode={widgetConfig.state?.colorMode}>
        <WidgetProvider value={widgetConfig}>
          <WidgetCustomPayloadContext.Provider value={mergedCustomPayload}>
            <InternalWidgetRenderer
              widgetConfig={widgetConfig}
              widgetDefinition={widgetDefinition}
              customPayload={mergedCustomPayload}
            >
              {widgetConfig.children?.map((child, i) => (
                <WidgetRenderer2
                  widgetConfig={child}
                  key={i}
                  customPayload={mergedCustomPayload}
                />
              ))}
            </InternalWidgetRenderer>
          </WidgetCustomPayloadContext.Provider>
        </WidgetProvider>
      </ColorMode>
    </ErrorBoundary>
  )
}
